import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { finalize } from 'rxjs/operators';

import { Invoice, InvoiceTypeEnum } from '@app/models';
import { AppService, ErrorHandlerService, NotyService, PaymentService } from '@app/services';
import { LoaderService } from '@app/shared';
import { TranslateService } from '@ngx-translate/core';

enum InvoiceStatusVariant {
  Paid = 'paid',
  Neutral = 'neutral',
  Warning = 'warning',
  Error = 'error'
}

@Component({
  selector: 'app-invoices',
  templateUrl: './invoices.component.html',
  styleUrls: ['./invoices.component.scss'],
  standalone: false
})
export class InvoicesComponent implements OnInit {
  @Input() invoices: Invoice[] = [];
  @Input() organizationUuid?: string;
  @Input() limit = 10;
  @Input() currency = 'BRL';
  @Input() restrictReceiptToBrl = false;
  @Input() showTitle = true;
  @Input() showContainer = true;
  @Input() isMobile = false;
  @Input() enableMobileRowClick = false;
  @Output() invoicesLoaded = new EventEmitter<Invoice[]>();

  isLoading = false;
  invoicePageNumber: number;
  isLastPage = false;
  readonly InvoiceTypeEnum = InvoiceTypeEnum;
  private readonly paidStatuses: readonly string[] = ['paid', 'authorized', 'active'];
  private readonly neutralStatuses: readonly string[] = ['expired'];
  private readonly warningStatuses: readonly string[] = ['pending', 'captured', 'warning', 'partially_paid', 'refunded', 'in_protest'];
  private readonly errorStatuses: readonly string[] = ['error', 'chargeback', 'canceled', 'disabled'];

  constructor(
    public translateService: TranslateService,
    private route: ActivatedRoute,
    private errorHandlerService: ErrorHandlerService,
    private loaderService: LoaderService,
    private paymentService: PaymentService,
    private appService: AppService,
    private notyService: NotyService
  ) {}

  ngOnInit() {
    this.loadInvoices();
  }

  loadInvoices(options: { page?: number; limit?: number; isBuyCredits?: boolean } = {}) {
    const organizationUuid = this.organizationUuid || this.route.snapshot.params.id;
    if (!organizationUuid) {
      this.invoices = [];
      this.isLastPage = true;
      return;
    }

    this.isLoading = true;
    this.loaderService.show();
    this.invoicePageNumber = options.page || 1;
    const limit = options.limit ?? this.limit;
    this.paymentService
      .organizationInvoices({ organizationUuid, limit, page: this.invoicePageNumber })
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.loaderService.hide();
        })
      )
      .subscribe({
        next: page => {
          this.invoices = (this.invoicePageNumber === 1 ? [] : this.invoices || []).concat(page.data);
          this.isLastPage = page.current_page >= page.last_page;
          this.invoicesLoaded.emit(this.invoices);
        },
        error: error => this.errorHandlerService.handle(error)
      });
  }

  onLoadMore() {
    this.loadInvoices({ page: this.invoicePageNumber + 1 });
  }

  nfseUrl(invoice: Invoice, fileType?: 'pdf' | 'xml') {
    if (fileType === 'xml') {
      return invoice.url_nfse.replace(/\.pdf$/, '.xml');
    }
    return invoice.url_nfse;
  }

  invoiceUrl(invoice: Invoice) {
    const nfseUrl = this.nfseUrl(invoice);
    if (invoice.url_payment && !nfseUrl && !['canceled', 'disabled', 'expired', 'error', 'paid'].includes(invoice.status)) {
      return invoice.url_payment;
    }
    return '';
  }

  canShowReceipt(invoice: Invoice) {
    if (!invoice?.url_nfse) {
      return false;
    }

    if (!this.restrictReceiptToBrl) {
      return true;
    }

    return this.currency === 'BRL';
  }

  mobileRowUrl(invoice: Invoice) {
    return this.invoiceUrl(invoice) || this.nfseUrl(invoice);
  }

  isMobileRowClickable(invoice: Invoice) {
    return this.isMobile && this.enableMobileRowClick && !!this.mobileRowUrl(invoice);
  }

  onMobileRowClick(invoice: Invoice) {
    if (!this.isMobileRowClickable(invoice)) {
      return;
    }

    this.openUrl(this.mobileRowUrl(invoice));
  }

  openUrl(url: string) {
    if (url) {
      open(url, '_blank');
    }
  }

  currencyCode(currency?: string) {
    return this.appService.currencyCodeFor(currency);
  }

  statusVariant(status?: string): InvoiceStatusVariant {
    const normalizedStatus = status || '';

    if (this.paidStatuses.includes(normalizedStatus)) {
      return InvoiceStatusVariant.Paid;
    }

    if (this.neutralStatuses.includes(normalizedStatus) || !normalizedStatus) {
      return InvoiceStatusVariant.Neutral;
    }

    if (this.warningStatuses.includes(normalizedStatus)) {
      return InvoiceStatusVariant.Warning;
    }

    if (this.errorStatuses.includes(normalizedStatus)) {
      return InvoiceStatusVariant.Error;
    }

    return InvoiceStatusVariant.Neutral;
  }

  statusClass(status?: string) {
    return `invoices__list-item--${this.statusVariant(status)}`;
  }

  statusBadge(status?: string) {
    const statusVariant = this.statusVariant(status);

    if (statusVariant === InvoiceStatusVariant.Paid) {
      return 'success';
    }

    if (statusVariant === InvoiceStatusVariant.Warning) {
      return 'warning';
    }

    if (statusVariant === InvoiceStatusVariant.Error) {
      return 'error';
    }

    return 'neutral';
  }

  statusIcon(status?: string) {
    const statusVariant = this.statusVariant(status);

    if (statusVariant === InvoiceStatusVariant.Paid) {
      return 'fa-check';
    }

    if (statusVariant === InvoiceStatusVariant.Neutral) {
      return 'fa-exclamation-circle';
    }

    if (statusVariant === InvoiceStatusVariant.Warning) {
      return 'fa-exclamation-triangle';
    }

    if (statusVariant === InvoiceStatusVariant.Error) {
      return 'fa-xmark';
    }

    return 'fa-exclamation-circle';
  }

  idBadgeVariant(status?: string) {
    if (this.warningStatuses.includes(status || '')) {
      return 'warning';
    }

    return 'neutral';
  }

  copyInvoiceId() {
    this.notyService.success(this.translateService.instant('shared.textCopySuccess'), 2000);
  }
}
